// One renderer per pages_json page type — App Flow §4, API §3.6.
import type { PuzzleContent } from '@roots/schema';
import { Card, LogoMark } from '@roots/ui';

type StoryPhoto = { photo_id: string; alt_text: string; url?: string; caption?: string | null };

export function CoverPage({
  theme,
  monthLabel,
  familyName,
  editorName,
  memberCount,
}: {
  theme: string;
  monthLabel: string;
  familyName: string;
  editorName: string;
  memberCount: number;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 roots-paper roots-cover rounded-[var(--radius-lg)] p-8 text-center">
      <p className="type-label">{familyName}</p>
      <h2 className="type-display text-[length:calc(var(--type-display-size)*1.4)]">{theme}</h2>
      <p className="type-caption">{monthLabel}</p>
      <p className="type-caption">
        Edited by {editorName} · {memberCount} members
      </p>
    </div>
  );
}

export function StoryPage({
  memberName,
  promptText,
  narrative,
  photos,
  layoutHint,
}: {
  memberName: string;
  promptText: string;
  narrative: string;
  photos: StoryPhoto[];
  layoutHint: string;
}) {
  const stacked = layoutHint === 'text_centered' || photos.length === 0;
  return (
    <article
      className={`flex h-full flex-col gap-4 p-6 ${stacked ? '' : 'md:flex-row md:items-start'} ${
        layoutHint === 'text_right_photo_left' ? 'md:flex-row-reverse' : ''
      }`}
    >
      <div className="flex-1">
        <p className="type-label">{promptText}</p>
        <h3 className="type-heading mt-2">{memberName}</h3>
        <p className="type-body mt-3 whitespace-pre-wrap">{narrative}</p>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-1 flex-col gap-2">
          {photos.map((photo) => (
            <figure key={photo.photo_id}>
              {photo.url ? (
                <img
                  src={photo.url}
                  alt={photo.alt_text}
                  loading="lazy"
                  className="w-full rounded-[var(--radius-md)] object-cover"
                />
              ) : (
                <div className="roots-skeleton h-40 w-full" />
              )}
              {photo.caption && (
                <figcaption className="type-caption mt-1">{photo.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </article>
  );
}

function Grid({ grid, reveal }: { grid: string[][]; reveal?: boolean }) {
  // Keys come from precomputed coordinates so the JSX never keys on an array index.
  const rows = grid.map((row, r) => ({
    id: `r${r}`,
    cells: row.map((cell, c) => ({ id: `c${r}-${c}`, value: cell })),
  }));
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse">
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {row.cells.map((cell) => (
                <td
                  key={cell.id}
                  className={`type-caption h-7 w-7 border border-[var(--color-accent-warm-gray)] text-center ${
                    cell.value === '' ? 'bg-[var(--color-accent-warm-gray)]' : ''
                  }`}
                >
                  {reveal ? cell.value : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PuzzlePage({ content }: { content: PuzzleContent }) {
  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <h3 className="type-heading">Puzzles</h3>
      <section className="flex flex-col gap-3">
        <h4 className="type-subheading">Crossword</h4>
        <Grid grid={content.crossword.grid} />
        <div className="grid gap-4 md:grid-cols-2">
          {(['across', 'down'] as const).map((dir) => (
            <div key={dir}>
              <p className="type-label">{dir}</p>
              <ol className="type-caption mt-1 flex flex-col gap-1">
                {content.crossword.entries
                  .filter((e) => e.direction === dir)
                  .map((e) => (
                    <li key={`${e.number}-${e.direction}`}>
                      {e.number}. {e.clue}
                    </li>
                  ))}
              </ol>
            </div>
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-3">
        <h4 className="type-subheading">Word search</h4>
        <Grid grid={content.word_search.grid} reveal />
        <p className="type-caption">
          Find: {content.word_search.words.map((w) => w.word).join(' · ')}
        </p>
      </section>
    </div>
  );
}

export function HoroscopePage({
  content,
}: {
  content: { horoscopes: { member_name: string; zodiac: string; text: string }[] };
}) {
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h3 className="type-heading">Horoscopes</h3>
      <div className="flex flex-col gap-3">
        {content.horoscopes.map((h) => (
          <Card key={h.member_name} className="flex flex-col gap-1">
            <p className="type-subheading">
              {h.member_name} · {h.zodiac}
            </p>
            <p className="type-body">{h.text}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function FoodPage({
  content,
}: {
  content: {
    recipe: {
      title: string;
      description: string;
      prep_time: string;
      ingredients: string[];
      steps: string[];
      dietary_note: string;
    };
  };
}) {
  const { recipe } = content;
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h3 className="type-heading">From the kitchen</h3>
      <h4 className="type-display">{recipe.title}</h4>
      <p className="type-body">{recipe.description}</p>
      <p className="type-caption">{recipe.prep_time}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <p className="type-label">Ingredients</p>
          <ul className="type-body mt-1 list-disc pl-5">
            {recipe.ingredients.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="type-label">Method</p>
          <ol className="type-body mt-1 list-decimal pl-5">
            {recipe.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>
      </div>
      <p className="type-caption">{recipe.dietary_note}</p>
    </div>
  );
}

export function TravelPage({
  content,
}: {
  content: {
    destination: {
      name: string;
      country: string;
      why_your_family_would_love_it: string;
      fun_fact: string;
    };
  };
}) {
  const d = content.destination;
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h3 className="type-heading">Somewhere to go</h3>
      <h4 className="type-display">
        {d.name}, {d.country}
      </h4>
      <p className="type-body">{d.why_your_family_would_love_it}</p>
      <Card>
        <p className="type-caption">Did you know? {d.fun_fact}</p>
      </Card>
    </div>
  );
}

export function PhotoCollectionPage({
  content,
}: {
  content: {
    collection_title: string;
    selected_photos: { photo_id: string; caption: string; alt_text?: string; url?: string }[];
  };
}) {
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h3 className="type-heading">{content.collection_title}</h3>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {content.selected_photos.map((photo) => (
          <figure key={photo.photo_id}>
            {photo.url ? (
              <img
                src={photo.url}
                alt={photo.alt_text ?? photo.caption}
                loading="lazy"
                className="aspect-square w-full rounded-[var(--radius-sm)] object-cover"
              />
            ) : (
              <div className="roots-skeleton aspect-square w-full" />
            )}
            {photo.caption && (
              <figcaption className="type-caption mt-1">{photo.caption}</figcaption>
            )}
          </figure>
        ))}
      </div>
    </div>
  );
}

export function OccasionsPage({
  content,
}: {
  content: { cards: { recipient_name: string; occasion_type: string; message: string }[] };
}) {
  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h3 className="type-heading">Celebrations</h3>
      <div className="flex flex-col gap-3">
        {content.cards.map((card) => (
          <Card key={`${card.recipient_name}-${card.occasion_type}`} className="text-center">
            <p className="type-label">{card.occasion_type}</p>
            <p className="type-display mt-2">{card.recipient_name}</p>
            <p className="type-body mt-2">{card.message}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function PlaceholderPage({ message }: { message: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="type-display">Back next month</p>
      <p className="type-body max-w-[32ch]">{message}</p>
    </div>
  );
}

export function CreditsPage({
  editorName,
  contributors,
}: {
  editorName: string;
  contributors: string[];
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="type-label">This edition</p>
      <p className="type-body">Edited by {editorName}</p>
      <p className="type-body">{contributors.join(' · ')}</p>
      <p className="type-caption mt-6 flex items-center gap-2">
        <LogoMark simple title={null} className="h-6 w-auto text-[var(--color-text-secondary)]" />
        Made with Roots
      </p>
    </div>
  );
}
